
/// <amd-module name="Environments/Providers/VirtualMachineGroups/Components/VirtualMachineGroupTargets" />
import * as React from "react";

import { VirtualMachine, TaskAgentJobRequest, TaskAgentStatus, TaskResult, VirtualMachineGroup } from "DistributedTask/Client/Contracts/TaskAgent";
import * as Resources from "Environments/Providers/VirtualMachineGroups/Resources";
import { StoreManager } from "Environments/Flux/StoreManager";
import { ActionCreatorManager } from "Environments/Flux/ActionCreatorManager";
import { VirtualMachineGroupStore } from "Environments/Providers/VirtualMachineGroups/Stores/VirtualMachineGroupStore";
import { VirtualMachineGroupActionCreator } from "Environments/Providers/VirtualMachineGroups/ActionsCreators/VirtualMachineGroupActionCreator";
import { targetsCount } from "Environments/Providers/VirtualMachineGroups/Utilities";
import { VirtualMachineTags } from "Environments/Providers/VirtualMachineGroups/Components/VirtualMachineTags";
import { AddTarget } from "Environments/Providers/VirtualMachineGroups/Components/AddTarget";
import { Observer } from "VSSUI/Observer";
import { IVssComponentContext, VssComponent, IVssComponentProperties } from "VSS/Platform/Layout";
import { ArrayItemProvider } from "VSSUI/Utilities/Provider";
import { Link } from "VSSUI/Link";
import { ITableColumn, Table, ColumnFill, SimpleTableCell } from "VSSUI/Table";
import { Card } from "VSSUI/Card";
import { ObservableValue } from "VSS/Core/Observable";
import { Icon } from "VSSUI/Icon";
import { Button } from "VSSUI/Button";
import { IHeaderCommandBarItem } from "VSSUI/HeaderCommandBar";
import "VSS/LoaderPlugins/Css!Environments/Providers/VirtualMachineGroups/Components/VirtualMachineGroupTargets";


interface IVirtualMachineGroupTargetsState {
    itemProvider: ArrayItemProvider<ITableItem>;
    currentTagsBymachineIds: Map<number, string[]>;
}

interface IVirtualMachineTargetsProps extends IVssComponentProperties {
    environmentId: number;
    virtualMachineGroup: VirtualMachineGroup;
}

interface ITableItem {
    virtualMachine: VirtualMachine;
}

export class VirtualMachineGroupTargets extends VssComponent<IVirtualMachineTargetsProps, IVirtualMachineGroupTargetsState> {
    constructor(props: IVirtualMachineTargetsProps, context: IVssComponentContext) {
        super(props, context);
        this.state = {
            itemProvider: new ArrayItemProvider<ITableItem>([]),
            currentTagsBymachineIds: new Map<number, string[]>()
        };

        this._actionCreator = ActionCreatorManager.CreateActionCreator<VirtualMachineGroupActionCreator>(VirtualMachineGroupActionCreator, this.getInstanceId());
        this._store = StoreManager.CreateStore<VirtualMachineGroupStore>(VirtualMachineGroupStore, this.getInstanceId());
        
        this._store.addChangedListener(this._onVirtualMachineGroupStoreGetChange);
        this._actionCreator.getVirtualMachines(this.context.pageContext, this.props.environmentId, this.props.virtualMachineGroup.id);
    }

    public getInstanceId() : string {
        return "virtualmachinegroup-targets-instance-id";
    }

    public render(): React.ReactNode {
        return (
                <div className="page-content page-content-top">
                    <Card
                        contentProps={ {contentPadding: false} }
                        className="bolt-card-no-vertical-padding flex-grow"
                        titleProps={{text: Resources.VirtualMachinesText}}
                        headerCommandBarItems={this._getCardHeaderBarItems()}
                        >
                        <div>
                            <Table<Partial<ITableItem>>
                                key={this.state.itemProvider.length}
                                columns={this._sizableColumns}
                                itemProvider={this.state.itemProvider}
                            />
                            {this._getShowMoreElement()}
                            {this._getAddTargetDialogComponent()}
                        </div>
                    </Card>
                </div>
            );
    }

    public componentWillUnmount(): void {
        this._store.removeListener("get", this._onVirtualMachineGroupStoreGetChange);
        StoreManager.DeleteStore<VirtualMachineGroupStore>(VirtualMachineGroupStore, this.getInstanceId());
        ActionCreatorManager.DeleteActionCreator<VirtualMachineGroupActionCreator>(VirtualMachineGroupActionCreator, this.getInstanceId());
        super.componentWillUnmount();
    }

    private _isTagUpdated = (): boolean => {
        return (this.state.currentTagsBymachineIds && this.state.currentTagsBymachineIds.size > 0);
    }

    private _getAddTargetDialogComponent(): JSX.Element | undefined {
        return (
            <Observer showPanel={this._addTargetPanelOpened}>
                {(props: { showPanel: boolean }) => (
                    props.showPanel && this.props.environmentId && <AddTarget virtualMachineGroup={this.props.virtualMachineGroup} onDismissPanel={this._onDismissAddTargetPanel} instanceId={this.getInstanceId()} />
                )}
            </Observer>
        );
    }

    private _onSaveButtonClicked = () => {
        let virtualMachineItems = this.state.itemProvider.value.filter((vm) => this.state.currentTagsBymachineIds.has(vm.virtualMachine.id));
        let updatedVirtualMachines: VirtualMachine[] = [];
        for(let virtualMachineItem of virtualMachineItems) {
            updatedVirtualMachines.push({
                id: virtualMachineItem.virtualMachine.id,
                tags: this.state.currentTagsBymachineIds.get(virtualMachineItem.virtualMachine.id,)
            } as VirtualMachine);
        }

        this._actionCreator.updateVirtualMachines(this.context.pageContext, this.props.environmentId, this.props.virtualMachineGroup.id, updatedVirtualMachines);
    }

    private _getCardHeaderBarItems = () => {
        const commandBarItems: IHeaderCommandBarItem[] = [
            {
                important: true,
                id: "addTarget",
                text: Resources.AddTarget,
                onActivate: () => {
                    this._onAddTargetClicked();
                },
                iconProps: {
                    iconName: "Add"
                }
            },
            {
                id: "SaveTargets",
                onActivate: () => {
                    this._onSaveButtonClicked();
                },
                iconProps: {
                    iconName: "Save"
                },
                text: Resources.SaveText,
                disabled: !this._isTagUpdated()
            }
        ];

        return commandBarItems;
    }

    private _renderNameColumn = (rowIndex: number, columnIndex: number, tableColumn: ITableColumn<ITableItem>, tableItem: ITableItem): JSX.Element => {
        return (
            <SimpleTableCell key={"environments-col-" + columnIndex} columnIndex={columnIndex} tableColumn={tableColumn}>
                {this._getStatusIcon(tableItem.virtualMachine.agent.status)}
                {tableItem.virtualMachine.agent.name}
            </SimpleTableCell>
        );
    }

    private _getStatusIcon(agentStatus: TaskAgentStatus) {
        if(agentStatus == TaskAgentStatus.Offline) {
            return (<Icon iconName="Blocked" className="fontSize target-icon-list target-offline" />);
        }
        else {
            return (<Icon iconName="CircleFill" className="fontSize target-icon-list target-online" />);
        }
    }

    private _getJobStatusIcon(jobStatus : TaskResult) {
        if(jobStatus == TaskResult.Succeeded || jobStatus == TaskResult.SucceededWithIssues) {
            return (<Icon iconName="Accept" className="target-job-status target-suceeded" />);
        }
        else {
            return (<Icon iconName="Cancel" className="target-job-status target-failed" />);
        }
    }

    private _renderSummaryColumn = (rowIndex: number, columnIndex: number, tableColumn: ITableColumn<ITableItem>, tableItem: ITableItem): JSX.Element => {
        let lastCompletedRequest: TaskAgentJobRequest = tableItem.virtualMachine.agent.lastCompletedRequest;
        if(!lastCompletedRequest) {
            return (
                <SimpleTableCell key={"vmg-col-" + columnIndex} columnIndex={columnIndex} tableColumn={tableColumn}>
                    <i>{Resources.NeverDeployed}</i>
                </SimpleTableCell>
            );
        }
        else {
            return (
                <SimpleTableCell key={"vmg-col-" + columnIndex} columnIndex={columnIndex} tableColumn={tableColumn}>
                    {this._getJobStatusIcon(lastCompletedRequest.result)}
                    <Link href={lastCompletedRequest.definition._links.web.href}>{tableItem.virtualMachine.agent.lastCompletedRequest.definition.name}</Link> &nbsp; / &nbsp;
                    <Link href={lastCompletedRequest.owner._links.web.href}>{tableItem.virtualMachine.agent.lastCompletedRequest.owner.name}</Link>
                </SimpleTableCell>
            );
        }
    }

    private _renderTagColumn = (rowIndex: number, columnIndex: number, tableColumn: ITableColumn<ITableItem>, tableItem: ITableItem): JSX.Element => {
        return (
            <SimpleTableCell key={"vmg-col-" + columnIndex} columnIndex={columnIndex} tableColumn={tableColumn} className="tags-container">
                <VirtualMachineTags resourceId={tableItem.virtualMachine.id} onTagUpdated={this._onTagChanged} tags={this.getCurrentTags(tableItem.virtualMachine)}/>
            </SimpleTableCell>
        );
    }

    private getCurrentTags(machine: VirtualMachine): string[] {
        if(this.state.currentTagsBymachineIds.has(machine.id)) {
            return this.state.currentTagsBymachineIds.get(machine.id)!;
        }

        return machine.tags || [];
    }

    private _onTagChanged = (machineId: number, tags: string[]) => {
        let virtualMachine = this.state.itemProvider.value.filter((tableItem) => {return tableItem.virtualMachine.id == machineId})[0].virtualMachine;
        this._actionCreator.virtualMachineTagUpdated(virtualMachine, tags);
    }

    private _sizableColumns = [
        { id: "Name", name: Resources.NameText, minWidth: 200, width: new ObservableValue(400), renderCell: this._renderNameColumn },
        { id: "LatestDeployment", name: Resources.LatestDeployment, minWidth: 200, width: new ObservableValue(400), renderCell: this._renderSummaryColumn },
        { id: "Tags", name: Resources.TagsText, minWidth: 200, width: new ObservableValue(400), renderCell: this._renderTagColumn},
        ColumnFill,
    ];

    private _onVirtualMachineGroupStoreGetChange = () => {
        let virtualMachineGroupTargetResult = this._store.getVirtualMachineGroupTargetsResult();
        const itemProvider = new ArrayItemProvider<ITableItem>(virtualMachineGroupTargetResult.virtualMachines.map<ITableItem>(virtualMachine => {
            return {
                virtualMachine: virtualMachine
            } as ITableItem;
        }));

        this.setState({
            itemProvider: itemProvider,
            currentTagsBymachineIds: this._store.getcurrentTagsBymachineIds()
        });

    }

    private _getShowMoreElement = (): React.ReactNode => {
        const continuationToken = this._store.getContinuationToken();

        if (continuationToken) {
            return (
                <div className={"show-more-button"}>
                    <Button
                        onClick={() => {
                            this._actionCreator.loadMoreVirtualMachines(
                                this.context.pageContext,
                                this.props.environmentId,
                                this.props.virtualMachineGroupId,
                                continuationToken,
                                targetsCount
                            );
                        }}
                    >
                        {Resources.ShowMore}
                    </Button>
                </div>
            );
        }
        
        return null;
    }

    private _onAddTargetClicked = (): void => {
        this._addTargetPanelOpened.value = true;
    }

    private _onDismissAddTargetPanel = (): void => {
        this._addTargetPanelOpened.value = false;
    }

    private readonly _addTargetPanelOpened = new ObservableValue<boolean>(false);
    private _store: VirtualMachineGroupStore;
    private _actionCreator: VirtualMachineGroupActionCreator;
}
